/* Flash Sale landing page: hero slider + countdown timer */

if (!customElements.get('fs-slider')) {
  customElements.define(
    'fs-slider',
    class FsSlider extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('.fs-hero__track');
        this.slides = Array.from(this.querySelectorAll('.fs-hero__slide'));
        this.dots = Array.from(this.querySelectorAll('.fs-hero__dot'));
        this.viewport = this.querySelector('.fs-hero__viewport');
        this.index = 0;
        if (this.slides.length < 2) return;

        // Slides may differ in height: size the viewport to the visible slide
        this.fitHeight = () => {
          const h = this.slides[this.index].offsetHeight;
          if (h) this.viewport.style.height = h + 'px';
        };
        this.querySelectorAll('img').forEach((img) => { if (!img.complete) img.addEventListener('load', this.fitHeight); });
        window.addEventListener('resize', this.fitHeight);
        this.fitHeight();

        this.querySelector('.fs-hero__arrow--prev')?.addEventListener('click', () => this.go(this.index - 1, true));
        this.querySelector('.fs-hero__arrow--next')?.addEventListener('click', () => this.go(this.index + 1, true));
        this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.go(i, true)));

        // Swipe on touch devices
        let startX = null;
        this.addEventListener('touchstart', (e) => { startX = e.touches[0].clientX; }, { passive: true });
        this.addEventListener('touchend', (e) => {
          if (startX === null) return;
          const dx = e.changedTouches[0].clientX - startX;
          if (Math.abs(dx) > 40) this.go(this.index + (dx < 0 ? 1 : -1), true);
          startX = null;
        });

        this.autoplay = this.dataset.autoplay === 'true';
        this.speed = (parseFloat(this.dataset.speed) || 5) * 1000;
        this.addEventListener('mouseenter', () => this.stop());
        this.addEventListener('mouseleave', () => this.start());
        this.start();
      }

      disconnectedCallback() {
        this.stop();
        if (this.fitHeight) window.removeEventListener('resize', this.fitHeight);
      }

      go(i, user) {
        const n = this.slides.length;
        this.index = ((i % n) + n) % n;
        if (!this.classList.contains('fs-hero--fade')) {
          this.track.style.transform = `translateX(-${this.index * 100}%)`;
        }
        this.slides.forEach((s, k) => s.classList.toggle('is-active', k === this.index));
        this.dots.forEach((d, k) => {
          d.classList.toggle('is-active', k === this.index);
          d.setAttribute('aria-current', k === this.index ? 'true' : 'false');
        });
        this.slides.forEach((s, k) => s.setAttribute('aria-hidden', k === this.index ? 'false' : 'true'));
        this.fitHeight();
        if (user) this.start(); // restart the autoplay timer after manual navigation
      }

      start() {
        this.stop();
        if (this.autoplay) this.timer = setInterval(() => this.go(this.index + 1), this.speed);
      }

      stop() { clearInterval(this.timer); }
    }
  );
}

if (!customElements.get('fs-countdown')) {
  customElements.define(
    'fs-countdown',
    class FsCountdown extends HTMLElement {
      connectedCallback() {
        // data-end looks like "2026-10-12T23:59:00+05:30"
        this.end = new Date(this.dataset.end).getTime();
        this.timerEl = this.querySelector('.fs-countdown__timer');
        this.endedEl = this.querySelector('.fs-countdown__ended');
        this.daysWrap = this.querySelectorAll('[data-days-part]');
        this.els = {
          d: this.querySelector('[data-unit="days"]'),
          h: this.querySelector('[data-unit="hours"]'),
          m: this.querySelector('[data-unit="minutes"]'),
          s: this.querySelector('[data-unit="seconds"]'),
        };
        if (isNaN(this.end)) return;
        this.tick();
        this.interval = setInterval(() => this.tick(), 1000);
      }

      disconnectedCallback() { clearInterval(this.interval); }

      tick() {
        let diff = Math.max(0, Math.floor((this.end - Date.now()) / 1000));
        if (diff <= 0) {
          clearInterval(this.interval);
          if (this.timerEl) this.timerEl.hidden = true;
          if (this.endedEl) this.endedEl.hidden = false;
          return;
        }
        const days = Math.floor(diff / 86400);
        diff -= days * 86400;
        const hours = Math.floor(diff / 3600);
        diff -= hours * 3600;
        const minutes = Math.floor(diff / 60);
        const seconds = diff - minutes * 60;
        const pad = (n) => String(n).padStart(2, '0');

        // Show the Days box only while more than a day remains
        this.daysWrap.forEach((el) => (el.hidden = days === 0));
        if (this.els.d) this.els.d.textContent = pad(days);
        if (this.els.h) this.els.h.textContent = pad(hours);
        if (this.els.m) this.els.m.textContent = pad(minutes);
        if (this.els.s) this.els.s.textContent = pad(seconds);
      }
    }
  );
}

/* Card buttons: show a spinner inside the clicked button while it works */
if (!window.__fsCardLoaders) {
  window.__fsCardLoaders = true;

  const stopLoading = (btn) => {
    if (!btn) return;
    btn.classList.remove('is-loading');
    btn.removeAttribute('aria-busy');
    clearTimeout(btn.__fsTimer);
    if (btn.__fsObserver) btn.__fsObserver.disconnect();
  };

  // Add to cart: product-form sets aria-disabled during the request and removes it when done
  document.addEventListener('submit', (e) => {
    const form = e.target.closest('.fs-card__form form');
    if (!form) return;
    const btn = form.querySelector('.fs-card__btn');
    if (!btn || btn.disabled || btn.classList.contains('is-loading')) return;
    btn.classList.add('is-loading');
    btn.setAttribute('aria-busy', 'true');

    const errorWrap = form.closest('.fs-card__form')?.querySelector('.product-form__error-message-wrapper');
    btn.__fsObserver = new MutationObserver(() => {
      const done = btn.getAttribute('aria-disabled') !== 'true';
      const failed = errorWrap && !errorWrap.hidden;
      if (done || failed) stopLoading(btn);
    });
    // Watch from the next tick, after product-form has set aria-disabled
    setTimeout(() => {
      btn.__fsObserver.observe(btn, { attributes: true, attributeFilter: ['aria-disabled'] });
      if (errorWrap) btn.__fsObserver.observe(errorWrap, { attributes: true, attributeFilter: ['hidden'] });
    }, 0);
    btn.__fsTimer = setTimeout(() => stopLoading(btn), 10000); // safety net
  }, true);

  // Buy it now: spinner while the browser heads to checkout
  document.addEventListener('click', (e) => {
    const link = e.target.closest('a.fs-card__btn--outline');
    if (!link || e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
    link.classList.add('is-loading');
    link.setAttribute('aria-busy', 'true');
  });
  // Coming back with the browser Back button restores the page from cache: clear spinners
  window.addEventListener('pageshow', () => {
    document.querySelectorAll('.fs-card__btn.is-loading').forEach(stopLoading);
  });
}
